import type { FileInfo } from './file-info'
import type { AssemblyModelCache, PartAssetCache } from './cache'
import { DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE } from './cache-scope'
import { extractPartName, filterGlbFiles, sortGlbFilesByKey } from './glb'
import type { ImportedModelDisplayType } from './load-plan'
import type { ComposedAssemblyResult } from './types'
import type { GlbNodeIconType } from '../feature-tree/glb-node-icon'

export type ImportedModelTabType = 'assembly' | 'part'

export interface ImportedModelTabDescriptor {
  id: string
  label: string
  type: ImportedModelTabType | null
  iconType: GlbNodeIconType | null
  loading: boolean
  sourceFileId: string
  scopeObjectId: string | null
}

type CacheBackedImportedModelTab = {
  id: string
  type: ImportedModelTabType | null
  iconType?: GlbNodeIconType | null
  sourceFileId?: string
  scopeObjectId?: string | null
}

/**
 * 归一化导入文件路径。
 *
 * @param fileId 导入文件 ID。
 * @returns 统一使用 `/` 分隔的文件路径。
 */
function normalizeImportedModelFileId(fileId: string): string {
  return fileId.replace(/\\/g, '/').trim()
}

/**
 * 读取导入文件路径深度。
 *
 * 设计说明：
 * 1. 后端导出的根装配 GLB 通常位于项目/STEP 根路径下；
 * 2. 子零件和子装配一般会多一层或多层组件目录；
 * 3. 用路径深度作为兜底优先级，可以避免纯字典序把 `6Dof_Robot/Axe_1.glb`
 *    排到根装配前面。
 *
 * @param fileId 导入文件 ID。
 * @returns 路径段数量。
 */
function resolveImportedModelPathDepth(fileId: string): number {
  return normalizeImportedModelFileId(fileId).split('/').filter(Boolean).length
}

/**
 * 读取不含扩展名的文件名。
 *
 * @param fileId 导入文件 ID。
 * @returns 文件基础名称。
 */
function resolveImportedModelBaseName(fileId: string): string {
  const normalizedFileId = normalizeImportedModelFileId(fileId)
  const fileName = normalizedFileId.split('/').filter(Boolean).at(-1) ?? normalizedFileId
  return fileName.replace(/(\.assembly\.cad|\.part\.cad|\.step|\.stp|\.glb|\.[^.]+)$/iu, '').trim().toLowerCase()
}

/**
 * 查找项目的权威 CAD 源文件。
 *
 * @param projectFiles 项目文件列表。
 * @returns 权威 CAD 源文件；不存在时返回 `undefined`。
 */
function findAuthoritativeCadSourceFile(projectFiles: FileInfo[]): FileInfo | undefined {
  return projectFiles.find((file) => (
    (file.kind === 'cad_assembly' || file.kind === 'cad_part')
    && file.fileId.trim().length > 0
  ))
}

/**
 * 计算导入模型作为默认标签的排序分数。
 *
 * @param file 当前 GLB 文件。
 * @param projectFiles 完整项目文件列表。
 * @returns 分数越小优先级越高。
 */
function scoreDefaultImportedModelFile(file: FileInfo, projectFiles: FileInfo[]): number {
  const authoritativeCadFile = findAuthoritativeCadSourceFile(projectFiles)
  const fileUuid = file.uuid?.trim().toLowerCase()
  const authoritativeUuid = authoritativeCadFile?.uuid?.trim().toLowerCase()
  const sameAuthoritativeUuid = Boolean(
    fileUuid
    && authoritativeUuid
    && fileUuid === authoritativeUuid,
  )
  if (sameAuthoritativeUuid) {
    return -10_000
  }

  const sourceBaseName = authoritativeCadFile
    ? resolveImportedModelBaseName(authoritativeCadFile.fileId)
    : ''
  const fileBaseName = resolveImportedModelBaseName(file.fileId)
  const hasMatchingSourceName = Boolean(sourceBaseName && fileBaseName === sourceBaseName)

  // 路径深度是最稳定的兜底信号；名称匹配只在同深度下辅助主模型胜出。
  return resolveImportedModelPathDepth(file.fileId) * 10 + (hasMatchingSourceName ? -1 : 0)
}

/**
 * 按默认打开优先级排序导入模型标签文件。
 *
 * @param glbFiles GLB 文件列表。
 * @param projectFiles 完整项目文件列表。
 * @returns 默认标签在首位的 GLB 文件列表。
 */
export function sortImportedModelTabFiles(
  glbFiles: FileInfo[],
  projectFiles: FileInfo[] = glbFiles,
): FileInfo[] {
  return sortGlbFilesByKey(glbFiles).sort((left, right) => {
    const scoreDelta =
      scoreDefaultImportedModelFile(left, projectFiles)
      - scoreDefaultImportedModelFile(right, projectFiles)
    if (scoreDelta !== 0) {
      return scoreDelta
    }
    return normalizeImportedModelFileId(left.fileId).localeCompare(
      normalizeImportedModelFileId(right.fileId),
    )
  })
}

/**
 * 解析默认打开的导入模型标签 ID。
 *
 * @param glbFiles GLB 文件列表。
 * @param projectFiles 完整项目文件列表。
 * @returns 默认标签 ID；不存在 GLB 时返回 `null`。
 */
export function resolveDefaultImportedModelTabId(
  glbFiles: FileInfo[],
  projectFiles: FileInfo[] = glbFiles,
): string | null {
  return sortImportedModelTabFiles(glbFiles, projectFiles)[0]?.fileId ?? null
}

/**
 * 构建导入模型标签描述。
 *
 * @param glbFiles GLB 文件列表。
 * @param projectFiles 完整项目文件列表。
 * @returns 标签描述列表。
 */
export function buildImportedModelTabs(
  glbFiles: FileInfo[],
  projectFiles: FileInfo[] = glbFiles,
): ImportedModelTabDescriptor[] {
  return sortImportedModelTabFiles(filterGlbFiles(glbFiles), projectFiles).map((file) => ({
    id: file.fileId,
    label: extractPartName(file.fileId),
    type: null,
    iconType: null,
    loading: false,
    sourceFileId: file.fileId,
    scopeObjectId: null,
  }))
}

export function resolveImportedModelTabType(
  displayType: ImportedModelDisplayType,
): ImportedModelTabType | null {
  if (displayType === 'assembly' || displayType === 'part') {
    return displayType
  }
  return null
}

export function collectResolvedPartTabTypesFromAssembly(
  result: Pick<ComposedAssemblyResult, 'manifest'>,
  fileIdByUuid: Map<string, string>,
): Array<{ tabId: string; type: ImportedModelTabType }> {
  const semanticsByFileUuid = new Map<string, { kinds: Set<ImportedModelTabType>; hasUnknown: boolean }>()

  Object.values(result.manifest.nodesById).forEach((node) => {
    const fileUuid = node.partFileUuid?.trim()
    if (!fileUuid) {
      return
    }
    const state = semanticsByFileUuid.get(fileUuid) ?? {
      kinds: new Set<ImportedModelTabType>(),
      hasUnknown: false,
    }
    if (node.semanticKind === 'assembly' || node.semanticKind === 'part') {
      state.kinds.add(node.semanticKind)
    } else {
      state.hasUnknown = true
    }
    semanticsByFileUuid.set(fileUuid, state)
  })

  return Array.from(semanticsByFileUuid.entries()).flatMap(([fileUuid, state]) => {
    const tabId = fileIdByUuid.get(fileUuid) ?? fileIdByUuid.get(fileUuid.toLowerCase())
    if (!tabId || state.hasUnknown || state.kinds.size !== 1) {
      return []
    }
    const [type] = Array.from(state.kinds)
    return [{ tabId, type }]
  })
}

export function resolveImportedModelTabTypeFromCaches(
  fileUuid: string,
  version: string,
  assemblyModelCache: Pick<AssemblyModelCache, 'peek'>,
  partAssetCache: Pick<PartAssetCache, 'peek'>,
): ImportedModelTabType | null {
  // Read-side consumer 显式按默认 project-scope 读取，避免跨 Tab 事务暂存态提前回填标签类型。
  if (assemblyModelCache.peek(fileUuid, version, DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE)) {
    return 'assembly'
  }
  if (partAssetCache.peek(fileUuid, version, DEFAULT_IMPORTED_MODEL_PUBLISH_SCOPE)) {
    return 'part'
  }
  return null
}

export function applyImportedModelTabCacheState<T extends CacheBackedImportedModelTab>(
  tabs: T[],
  glbFiles: FileInfo[],
  version: string,
  assemblyModelCache: Pick<AssemblyModelCache, 'peek'>,
  partAssetCache: Pick<PartAssetCache, 'peek'>,
): T[] {
  const fileById = new Map(glbFiles.map((file) => [file.fileId, file]))

  return tabs.map((tab) => {
    if (tab.scopeObjectId) {
      return tab
    }

    const sourceFileId = tab.sourceFileId ?? tab.id
    const file = fileById.get(sourceFileId)
    if (!file) {
      return tab
    }

    const nextType = resolveImportedModelTabTypeFromCaches(
      file.uuid,
      version,
      assemblyModelCache,
      partAssetCache,
    )
    const nextIconType = nextType

    if (tab.type === nextType && tab.iconType === nextIconType) {
      return tab
    }

    return {
      ...tab,
      type: nextType,
      iconType: nextIconType,
    } as T
  })
}
